import { useEffect, useMemo } from 'react'
import type { Color } from '@deck.gl/core/typed'
import { HeatmapLayer } from '@deck.gl/aggregation-layers/typed'
import { GoogleMapsOverlay } from '@deck.gl/google-maps/typed'
import { useGoogleMap } from '@react-google-maps/api'

export type HeatmapPoint = {
  position: [number, number]
  weight: number
}

const heatmapColorRange: Color[] = [
  [0, 255, 255, 0],
  [0, 255, 255, 80],
  [0, 191, 255, 110],
  [0, 255, 0, 130],
  [255, 255, 0, 150],
  [255, 128, 0, 170],
  [255, 0, 0, 190],
]

type Props = {
  data: google.maps.visualization.WeightedLocation[]
  radius: number
}

const hasLocation = (
  point: google.maps.visualization.WeightedLocation,
): point is google.maps.visualization.WeightedLocation & { location: google.maps.LatLng } => (
  point.location !== null
)

const DeckGlHeatmapLayer = ({ data, radius }: Props) => {
  const map = useGoogleMap()
  const heatmapData = useMemo<HeatmapPoint[]>(() => data
    .filter(hasLocation)
    .map((point): HeatmapPoint => ({
      position: [point.location.lng(), point.location.lat()],
      weight: point.weight,
    }))
    .filter((point) => (
      Number.isFinite(point.position[0])
      && Number.isFinite(point.position[1])
    )), [data])
  const overlay = useMemo(() => new GoogleMapsOverlay({
    interleaved: false,
    style: {
      pointerEvents: 'none',
    },
  }), [])
  const layers = useMemo(() => {
    if (heatmapData.length === 0) {
      return []
    }

    return [
      new HeatmapLayer<HeatmapPoint>({
        id: 'biz-plus-heatmap-layer',
        data: heatmapData,
        aggregation: 'SUM',
        colorRange: heatmapColorRange,
        debounceTimeout: 300,
        intensity: 1.2,
        radiusPixels: radius,
        threshold: 0.03,
        getPosition: (point) => point.position,
        getWeight: (point) => point.weight,
      }),
    ]
  }, [heatmapData, radius])

  useEffect(() => {
    overlay.setMap(map)
    return () => {
      overlay.setMap(null)
    }
  }, [map, overlay])

  useEffect(() => {
    overlay.setProps({ layers })
    return () => {
      overlay.setProps({ layers: [] })
    }
  }, [layers, overlay])

  useEffect(() => () => {
    overlay.finalize()
  }, [overlay])

  return null
}

export default DeckGlHeatmapLayer
